import { sendRequest } from "@infrastructure/api/api";
import ApiResponse from "@core/types/apiResponse";
import { ApiMessageResponse, ApiSuccessMessageResponse } from "@core/types/apiMessageResponse";
import { ReportTagResponse } from "@features/report/tag/types/reportTag";
import { ReportTagStatusResponse } from "@features/report/tag/types/reportTagStatus";
import { DepartmentReportStatusSummary } from "@features/report/tag/types/departmentReportStatusSummary";

const toQueryString = (params: Record<string, any>) => {
    const query = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return;
        query.set(key, String(value));
    });

    return query.toString();
};

/**
 * Fetches report tag data for a specific nation using query parameters.
 *
 * Constructs a GET request to `nation/{nationId}/reporttag/query` with the provided
 * query parameters serialized via `URLSearchParams`.
 *
 * @param nationId - The numeric identifier of the nation for which to query report tags.
 * @param params - Key-value pairs to be serialized into the query string (must be compatible with `URLSearchParams`).
 * @returns A promise resolving to an `ApiResponse<ReportTagResponse>` containing the fetched report tag data.
 * @throws Will rethrow any network or serialization errors encountered during the request.
 */
export const fetchChurchReportTag = async (churchId: number, params: any): Promise<ApiResponse<ReportTagResponse>> => {
    const query = toQueryString(params ?? {});
    const response = await sendRequest<any, ReportTagResponse>('GET', `church/${churchId}/reporttag/query${query ? `?${query}` : ''}`);
    return response;
};
export const fetchDistrictReportTag = async (districtId: number, params: any): Promise<ApiResponse<ReportTagResponse>> => {
    const query = toQueryString(params ?? {});
    const response = await sendRequest<any, ReportTagResponse>('GET', `district/${districtId}/reporttag/query${query ? `?${query}` : ''}`);
    return response;
};
export const fetchNationReportTag = async (nationId: number, params: any): Promise<ApiResponse<ReportTagResponse>> => {
    const query = toQueryString(params ?? {});
    const response = await sendRequest<any, ReportTagResponse>('GET', `nation/${nationId}/reporttag/query${query ? `?${query}` : ''}`);
    return response;
};

export const fetchReportTagStatus = async (params: any): Promise<ApiResponse<ReportTagStatusResponse>> => {
    const query = toQueryString(params ?? {});
    const response = await sendRequest<any, ReportTagStatusResponse>('GET', `reporttag/status/query${query ? `?${query}` : ''}`);
    return response;
};

/**
 * Creates a new report tag for the specified nation.
 *
 * Sends a POST request to `nation/{nationId}/reporttag/create` with the provided payload,
 * returning the API's standardized response containing the created report tag data.
 *
 * @param nationId - The identifier of the nation for which the report tag should be created.
 * @param data - The request payload containing fields required to create the report tag.
 * @returns A promise that resolves to an `ApiResponse` with `ReportTagResponse` data.
 * @throws May propagate network or server errors encountered during the request.
 */
export const createReportTag = async (nationId: number, data: any): Promise<ApiResponse<ApiMessageResponse>> => {
    const response = await sendRequest<any, ApiMessageResponse>('POST', `nation/${nationId}/reporttag/create`, data);
    return response;
};

/**
 * Updates an existing report tag for a given nation.
 *
 * Sends a POST request to `nation/{nationId}/reporttag/{reportTagId}/update`
 * with the provided payload to modify the report tag.
 *
 * @param nationId - The numeric identifier of the nation that owns the report tag.
 * @param reportTagId - The unique identifier of the report tag to be updated.
 * @param data - The payload containing fields to update on the report tag.
 * @returns A promise that resolves to the API response containing the updated `ReportTag`.
 * @throws Propagates any errors thrown by the underlying request, such as network or server errors.
 */
export const updateReportTag = async (nationId: number, reportTagId: number, data: any): Promise<ApiResponse<ApiSuccessMessageResponse>> => {
    const response = await sendRequest<any, ApiSuccessMessageResponse>('POST', `nation/${nationId}/reporttag/${reportTagId}/update`, data);
    return response;
};

export const fetchReportTagDepartmentReportStatusSummary = async (
    nationId: number,
    reportTagId: number
): Promise<ApiResponse<DepartmentReportStatusSummary>> => {
    const response = await sendRequest<any, DepartmentReportStatusSummary>(
        'GET',
        `nation/${nationId}/reporttag/${reportTagId}/department-report/status-summary`
    );
    return response;
};

export const requestReportTagReopen = async (nationId: number, reportTagId: number, data: any): Promise<ApiResponse<ApiSuccessMessageResponse>> => {
    const response = await sendRequest<any, ApiSuccessMessageResponse>('POST', `nation/${nationId}/reporttag/${reportTagId}/update/request`, data);
    return response;
};

export const approveReportTagReopen = async (nationId: number, reportTagId: number): Promise<ApiResponse<ApiSuccessMessageResponse>> => {
    const response = await sendRequest<any, ApiSuccessMessageResponse>('POST', `nation/${nationId}/reporttag/${reportTagId}/update/approve`);
    return response;
};

export const denyReportTagReopen = async (nationId: number, reportTagId: number): Promise<ApiResponse<ApiSuccessMessageResponse>> => {
    const response = await sendRequest<any, ApiSuccessMessageResponse>('POST', `nation/${nationId}/reporttag/${reportTagId}/update/deny`);
    return response;
};